import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { ChatLabels, FeedbackVote, Message } from '../types';
import { MessageBubble } from './MessageBubble';

interface MessageListProps {
  messages: Message[];
  labels: ChatLabels;
  markdown: boolean;
  avatar?: ReactNode;
  suggestedPrompts?: string[];
  isStreaming: boolean;
  renderMessage?: (message: Message) => ReactNode;
  onVote: (messageId: string, vote: FeedbackVote, comment?: string) => void;
  onSuggestion: (prompt: string) => void;
}

const STICKY_THRESHOLD_PX = 80;

export function MessageList({
  messages,
  labels,
  markdown,
  avatar,
  suggestedPrompts,
  isStreaming,
  renderMessage,
  onVote,
  onSuggestion,
}: MessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef(true);

  const onScroll = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
    stickyRef.current = distance < STICKY_THRESHOLD_PX;
  }, []);

  // Follow new content, but never yank the view away from someone reading back.
  useEffect(() => {
    const element = scrollRef.current;
    if (!element || !stickyRef.current) return;
    element.scrollTop = element.scrollHeight;
  }, [messages]);

  const showSuggestions =
    suggestedPrompts &&
    suggestedPrompts.length > 0 &&
    !messages.some((m) => m.role === 'user') &&
    !isStreaming;

  return (
    <div className="cbw-list" ref={scrollRef} onScroll={onScroll}>
      <div className="cbw-list__inner">
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            labels={labels}
            markdown={markdown}
            avatar={avatar}
            renderMessage={renderMessage}
            onVote={(vote, comment) => onVote(message.id, vote, comment)}
          />
        ))}

        {showSuggestions && (
          <div className="cbw-suggestions">
            {suggestedPrompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                className="cbw-suggestion"
                onClick={() => onSuggestion(prompt)}
              >
                {prompt}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Announces replies to screen readers without duplicating the visual list. */}
      <div className="cbw-sr-only" role="status" aria-live="polite" aria-atomic="false">
        {isStreaming ? labels.typing : ''}
      </div>
    </div>
  );
}
